import { ApiService } from '@/shared/services/api.service';
import { CxAgentStatusMap } from '../stores/CxFloorStore';

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

import {
  Integration,
  IntegrationAuthLoginDto,
  IntegrationDetails,
  SaveAgentMappingsDto,
  VOAgent,
  Client,
  Campaign,
  CreateCampaignDto,
  Kpi,
  QueueEntity,
  QueueAgentMapping,
  CreateQueueDto,
  AssignAgentsToQueueDto,
  AgentKpiEvaluationResult,
  VOUser,
  AssignSupervisorToQueuesDto,
  QueueDashboardMetrics,
  IntegrationOrg,
  CreateIntegrationOrgDto,
  UpdateIntegrationOrgDto,
  ToggleIntegrationDto,
  TickerMessage,
  CreateTickerMessageDto,
  TickerMessageCustom,
  CampaignDashboardSummary,
  CampaignDetails,
} from '../models/CXModel';

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  integrationOrgId?: number;
  integrationType?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
}

// ======= CX Integrations =======
export const fetchIntegrationsList = async (
  params: PaginationParams = {}
): Promise<PaginatedResponse<Integration>> => {
  const { page = 1, limit = 15 } = params;
  const resp = await ApiService.get('cx-integrations', { page, limit });
  return resp || { items: [], total: 0 };
};

export const toggleIntegration = async (toggleDto: ToggleIntegrationDto) => {
  const resp = await ApiService.put('cx-integrations/toggle', toggleDto);
  return resp;
};

export const authorizeIntegration = async (authLoginDto: IntegrationAuthLoginDto) => {
  const resp = await ApiService.post('cx-integrations/auth/login', authLoginDto);
  return resp;
};

export const fetchIntegrationDetails = async (
  integration: string
): Promise<IntegrationDetails | null> => {
  const resp = await ApiService.get(`cx-integrations/${integration}`);
  return resp;
};

export const disconnectIntegration = async (integrationId: number) => {
  const resp = await ApiService.delete(`cx-integrations/${integrationId}`);
  return resp;
};

// ======= CX Integration Orgs =======
export const fetchIntegrationOrgsList = async (
  params: PaginationParams = {}
): Promise<PaginatedResponse<IntegrationOrg>> => {
  const { page = 1, limit = 15, search, integrationType } = params;
  const query: Record<string, string | number> = { page, limit };
  if (search?.trim()) {
    query.search = search.trim();
  }
  if (integrationType && integrationType !== 'all') {
    query.integrationType = integrationType;
  }
  const resp = await ApiService.get('cx-integration-orgs', query);
  return resp || { items: [], total: 0 };
};

export const fetchIntegrationOrg = async (integrationOrgId: number): Promise<IntegrationOrg> => {
  const resp = await ApiService.get(`cx-integration-orgs/${integrationOrgId}`);
  return resp;
};

export const createIntegrationOrg = async (
  createDto: CreateIntegrationOrgDto
): Promise<IntegrationOrg> => {
  const resp = await ApiService.post('cx-integration-orgs', createDto);
  return resp;
};

export const updateIntegrationOrg = async (
  integrationOrgId: number,
  updateDto: UpdateIntegrationOrgDto
): Promise<IntegrationOrg> => {
  const resp = await ApiService.put(`cx-integration-orgs/${integrationOrgId}`, updateDto);
  return resp;
};

export interface ConfigSyncResult {
  integrationOrgId: number;
  integration: string;
  campaigns: { created: number; updated: number; removed: number };
  queues: { created: number; updated: number; removed: number };
  memberships: { agentsLinked: number; unmappedAgentsSkipped: number };
  warnings: string[];
}

export interface ConfigSyncJobStatus {
  jobId: string;
  state: 'queued' | 'active' | 'completed' | 'failed' | 'not_found';
  result?: ConfigSyncResult;
  failedReason?: string;
}

/**
 * Enqueue an async auto-sync of campaigns, queues and agent-in-queue memberships from
 * the provider. Returns a job handle to poll with getConfigSyncJobStatus.
 */
export const syncIntegrationOrgConfig = async (
  integrationOrgId: number
): Promise<ConfigSyncJobStatus> => {
  const resp = await ApiService.post(`cx-integration-orgs/${integrationOrgId}/sync`, {});
  return resp;
};

/** Poll the status/result of an enqueued config-sync job. */
export const getConfigSyncJobStatus = async (
  integrationOrgId: number,
  jobId: string
): Promise<ConfigSyncJobStatus> => {
  const resp = await ApiService.get(`cx-integration-orgs/${integrationOrgId}/sync/${jobId}`);
  return resp;
};

export const deleteIntegrationOrg = async (integrationOrgId: number): Promise<void> => {
  const resp = await ApiService.delete(`cx-integration-orgs/${integrationOrgId}`);
  return resp;
};

// ======= CX Agents =======
export const fetchIntegrationAgents = async (integrationOrgId: number, nextToken?: string) => {
  const queryParams = nextToken ? { nextToken } : undefined;
  const resp = await ApiService.get(`cx-agents/${integrationOrgId}`, queryParams);
  return resp;
};

export const fetchVOAgents = async (page = 1, limit = 99999): Promise<PaginatedResult<VOAgent>> => {
  const resp = await ApiService.get('cx-agents/vo-agents', { page, limit });
  return resp || { items: [], total: 0, page: 1, limit, totalPages: 0 };
};

export const saveAgentMapping = async (
  integrationOrgId: number,
  saveAgentMappingsDto: SaveAgentMappingsDto
) => {
  const resp = await ApiService.post(`cx-agents/${integrationOrgId}/mapping`, saveAgentMappingsDto);
  return resp;
};

/** Hide or unhide one or more provider integration agents on the mapping screen. */
export const setAgentVisibility = async (
  integrationOrgId: number,
  integrationAgentIds: string[],
  hidden: boolean
): Promise<{ success: boolean }> => {
  const resp = await ApiService.post(`cx-agents/${integrationOrgId}/agent-visibility`, {
    integration_agent_ids: integrationAgentIds,
    hidden,
  });
  return resp;
};

export const triggerAgentStatisticsCollection = async () => {
  const resp = await ApiService.post('cx-agents/statistics/collect');
  return resp;
};

// ======= CX Clients =======
export function fetchClientsList(): Promise<Client[]>;
export function fetchClientsList(
  page: number,
  limit?: number,
  search?: string
): Promise<PaginatedResult<Client>>;
export async function fetchClientsList(
  page?: number,
  limit?: number,
  search?: string
): Promise<Client[] | PaginatedResult<Client>> {
  const paginated = typeof page === 'number' || typeof limit === 'number';
  const reqPage = page ?? 1;
  const reqLimit = limit ?? (paginated ? 50 : 99999);
  const query: Record<string, string | number> = { page: reqPage, limit: reqLimit };
  if (search?.trim()) {
    query.search = search.trim();
  }
  const resp = await ApiService.get('cx-clients', query);

  if (Array.isArray(resp)) {
    return paginated
      ? { items: resp, total: resp.length, page: 1, limit: resp.length, totalPages: 1 }
      : resp;
  }

  const result: PaginatedResult<Client> = resp || {
    items: [],
    total: 0,
    page: reqPage,
    limit: reqLimit,
    totalPages: 0,
  };

  return paginated ? result : result.items;
}

export const createClient = async (clientData: {
  name: string;
  description: string;
}): Promise<Client> => {
  const resp = await ApiService.post('cx-clients', clientData);
  return resp;
};

export const updateClient = async (
  id: number,
  clientData: { name: string; description: string }
): Promise<Client> => {
  const resp = await ApiService.put(`cx-clients/${id}`, clientData);
  return resp;
};

export const deleteClient = async (id: number): Promise<void> => {
  const resp = await ApiService.delete(`cx-clients/${id}`);
  return resp;
};

// ======= CX Campaigns =======
export const fetchCampaignsList = async (
  params: PaginationParams = {}
): Promise<PaginatedResponse<Campaign>> => {
  const { page = 1, limit = 15, search, integrationOrgId } = params;
  const query: Record<string, string | number> = { page, limit };
  if (search?.trim()) {
    query.search = search.trim();
  }
  if (integrationOrgId) {
    query.integrationOrgId = integrationOrgId;
  }
  const resp = await ApiService.get('cx-campaigns', query);
  return resp || { items: [], total: 0 };
};

export const createCampaign = async (campaignData: CreateCampaignDto): Promise<Campaign> => {
  const resp = await ApiService.post('cx-campaigns', campaignData);
  return resp;
};

export const updateCampaign = async (
  id: number,
  campaignData: CreateCampaignDto
): Promise<Campaign> => {
  const resp = await ApiService.put(`cx-campaigns/${id}`, campaignData);
  return resp;
};

export const deleteCampaign = async (id: number): Promise<void> => {
  const resp = await ApiService.delete(`cx-campaigns/${id}`);
  return resp;
};

export const duplicateCampaign = async (id: number): Promise<Campaign> => {
  const resp = await ApiService.post(`cx-campaigns/${id}/duplicate`, {});
  return resp;
};

// ======= CX KPIs =======
export const fetchAvailableKpis = async (integrationName: string): Promise<Kpi[]> => {
  const resp = await ApiService.get(`cx-kpi/available/${integrationName}`);
  return resp || [];
};

export const fetchLatestKpisForAllAgents = async (
  floorPids?: string[]
): Promise<AgentKpiEvaluationResult[]> => {
  const queryParams = floorPids && floorPids.length > 0 ? `?floorPids=${floorPids.join(',')}` : '';
  const resp = await ApiService.get(`cx-kpi/agents-kpis${queryParams}`);
  return resp || [];
};

/**
 * Live dialer statuses for every mapped agent in the org, keyed by VO user pid.
 * Served from Redis on the backend, so this is cheap to call on every floor load.
 */
export const fetchAgentStatuses = async (): Promise<CxAgentStatusMap> => {
  const resp = await ApiService.get('cx-agent-status/statuses');
  return resp?.statuses || {};
};

// ======= CX Queues =======
export const fetchQueuesList = async (
  page = 1,
  limit = 50
): Promise<PaginatedResult<QueueEntity>> => {
  const resp = await ApiService.get('cx-queues', { page, limit });
  return resp || { items: [], total: 0, page: 1, limit, totalPages: 0 };
};

export const createQueue = async (queueData: CreateQueueDto): Promise<QueueEntity> => {
  const resp = await ApiService.post('cx-queues', queueData);
  return resp;
};

/** Replaces the queue's whole campaign set. Manually-created queues only. */
export const updateQueueCampaigns = async (
  id: number,
  campaignIds: number[]
): Promise<QueueEntity> => {
  const resp = await ApiService.patch(`cx-queues/${id}/campaigns`, { campaign_ids: campaignIds });
  return resp;
};

export const deleteQueue = async (id: number): Promise<void> => {
  const resp = await ApiService.delete(`cx-queues/${id}`);
  return resp;
};

// ======= CX Queue Agent Management =======
export function fetchAgentMappings(): Promise<QueueAgentMapping[]>;
export function fetchAgentMappings(
  page: number,
  limit?: number
): Promise<PaginatedResult<QueueAgentMapping>>;
export async function fetchAgentMappings(
  page?: number,
  limit?: number
): Promise<QueueAgentMapping[] | PaginatedResult<QueueAgentMapping>> {
  const paginated = typeof page === 'number' || typeof limit === 'number';
  const reqPage = page ?? 1;
  const reqLimit = limit ?? (paginated ? 50 : 99999);
  const resp = await ApiService.get('cx-queues/agents', { page: reqPage, limit: reqLimit });

  if (Array.isArray(resp)) {
    return paginated
      ? { items: resp, total: resp.length, page: 1, limit: resp.length, totalPages: 1 }
      : resp;
  }

  const result: PaginatedResult<QueueAgentMapping> = resp || {
    items: [],
    total: 0,
    page: reqPage,
    limit: reqLimit,
    totalPages: 0,
  };

  return paginated ? result : result.items;
}

export const assignAgentsToQueue = async (
  assignAgentsDto: AssignAgentsToQueueDto
): Promise<void> => {
  const resp = await ApiService.patch('cx-queues/agents/queue', assignAgentsDto);
  return resp;
};

/**
 * Remove agents from a single specific queue, keeping their other queue memberships.
 */
export const removeAgentsFromQueue = async (
  agentMappingIds: number[],
  queueId: number
): Promise<void> => {
  const resp = await ApiService.patch('cx-queues/agents/queue/remove', {
    agent_mapping_ids: agentMappingIds,
    queue_id: queueId,
  });
  return resp;
};

export const toggleAgentTraining = async (
  agentMappingId: number,
  training: boolean
): Promise<void> => {
  const resp = await ApiService.patch('cx-queues/agents/training', {
    agent_mapping_id: agentMappingId,
    training,
  });
  return resp;
};

// ======= CX Queue Supervisor Management =======
export function fetchSupervisors(): Promise<VOUser[]>;
export function fetchSupervisors(page: number, limit?: number): Promise<PaginatedResult<VOUser>>;
export async function fetchSupervisors(
  page?: number,
  limit?: number
): Promise<VOUser[] | PaginatedResult<VOUser>> {
  const paginated = typeof page === 'number' || typeof limit === 'number';
  const reqPage = page ?? 1;
  const reqLimit = limit ?? (paginated ? 50 : 99999);
  const resp = await ApiService.get('cx-queues/supervisors', { page: reqPage, limit: reqLimit });

  if (Array.isArray(resp)) {
    return paginated
      ? { items: resp, total: resp.length, page: 1, limit: resp.length, totalPages: 1 }
      : resp;
  }

  const result: PaginatedResult<VOUser> = resp || {
    items: [],
    total: 0,
    page: reqPage,
    limit: reqLimit,
    totalPages: 0,
  };

  return paginated ? result : result.items;
}

export const assignSupervisorToQueues = async (
  assignSupervisorDto: AssignSupervisorToQueuesDto
): Promise<void> => {
  const resp = await ApiService.patch('cx-queues/supervisor/queues', assignSupervisorDto);
  return resp;
};

// ======= CX Queues Dashboard =======
export const fetchQueuesDashboard = async (): Promise<QueueDashboardMetrics[]> => {
  const resp = await ApiService.get('cx-queues/dashboard');
  return resp || [];
};

// ======= CX Ticker Messages =======
export const fetchTickerMessages = async (
  floorPid: string,
  params: PaginationParams = {}
): Promise<PaginatedResponse<TickerMessage>> => {
  const { page = 1, limit = 15 } = params;
  const resp = await ApiService.get('cx-ticker-messages', { floorPid, page, limit });
  return resp || { items: [], total: 0 };
};

export const createTickerMessage = async (
  messageData: CreateTickerMessageDto
): Promise<TickerMessage> => {
  const resp = await ApiService.post('cx-ticker-messages', messageData);
  return resp;
};

export const updateTickerMessage = async (
  id: number,
  floorPid: string,
  messageData: Partial<TickerMessage>
): Promise<TickerMessage> => {
  const resp = await ApiService.patch(`cx-ticker-messages/${id}?floorPid=${floorPid}`, messageData);
  return resp;
};

export const deleteTickerMessage = async (id: number, floorPid: string): Promise<void> => {
  const resp = await ApiService.delete(`cx-ticker-messages/${id}?floorPid=${floorPid}`);
  return resp;
};

// ======= CX Custom Ticker Messages (Templates) =======
export const fetchCustomTickerMessages = async (): Promise<TickerMessageCustom[]> => {
  const resp = await ApiService.get('cx-ticker-messages/custom');
  return resp || [];
};

export const createCustomTickerMessage = async (message: string): Promise<TickerMessageCustom> => {
  const resp = await ApiService.post('cx-ticker-messages/custom', { custom_message: message });
  return resp;
};

export const updateCustomTickerMessage = async (
  id: number,
  message: string
): Promise<TickerMessageCustom> => {
  const resp = await ApiService.patch(`cx-ticker-messages/custom/${id}`, {
    custom_message: message,
  });
  return resp;
};

export const deleteCustomTickerMessage = async (id: number): Promise<void> => {
  const resp = await ApiService.delete(`cx-ticker-messages/custom/${id}`);
  return resp;
};

// ======= CX Boardroom Dashboard =======
export const fetchCampaignsDashboard = async (): Promise<CampaignDashboardSummary[]> => {
  const resp = await ApiService.get('cx-kpi/campaigns-dashboard');
  return resp || [];
};

export const fetchCampaignDetails = async (campaignId: number): Promise<CampaignDetails> => {
  const resp = await ApiService.get(`cx-kpi/campaign/${campaignId}/details`);
  return resp;
};
